import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import "../css/PunchedAttendenceStateWise.css";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel, FaPrint, FaSearch } from "react-icons/fa";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";

const PunchedAttendenceStateWise = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthOptions] = useState([
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ]);
  const [yearOptions, setYearOptions] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(31);

  // Pagination and Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef(null);
  const handleExpandSearch = () => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  // Generate Years from 2021 to current
  const generateYears = () => {
    const startYear = 2021;
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = startYear; year <= currentYear; year++) {
      years.push(year);
    }

    setYearOptions(years);
  };

  // Calculate days in selected month/year
  const calculateDaysInMonth = useCallback(() => {
    const days = new Date(selectedYear, selectedMonth, 0).getDate();
    setDaysInMonth(days);
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    generateYears();
    calculateDaysInMonth();
  }, [calculateDaysInMonth]);

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) return;
    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setCurrentPage(1);
    setSearchQuery("");

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`
      );
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setCurrentPage(1);
    setSearchQuery("");

    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";

    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let url = `${BASE_URL}/api/pob-targets/employee-datewise?`;

      if (selectedState) url += `state_id=${selectedState}&`;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        if (division?.division_name)
          url += `division=${encodeURIComponent(division.division_name)}&`;
      }
      if (selectedMonth) url += `month=${selectedMonth}&`;
      if (selectedYear) url += `year=${selectedYear}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;

      // Add pagination parameters
      const skip = (currentPage - 1) * itemsPerPage;
      url += `skip=${skip}&limit=${itemsPerPage}`;

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
      });

      // Handle response
      const data = response.data.data || response.data;
      setTableData(data);
      setTotalRecords(response.data.total_records || data.length);
      setShowContainers(true);
    } catch (err) {
      console.error("Failed to fetch data", err);
      if (err.response) {
        setError(
          `Server error: ${err.response.status} - ${err.response.statusText}`
        );
      } else if (err.request) {
        setError("No response received from server");
      } else {
        setError(`Request error: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    handleGoClick();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Re-fetch data when pagination or search changes
  useEffect(() => {
    if (showContainers) {
      handleGoClick();
    }
  }, [currentPage, itemsPerPage, searchQuery]);

  // Render day headers dynamically
  const renderDayHeaders = () => {
    const headers = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayShortName = date.toLocaleDateString("en-US", {
        weekday: "short",
      });
      const isSunday = date.getDay() === 0;

      headers.push(
        <th
          key={`day-${day}`}
          className={`PunchedAttendenceStateWise-day-header ${
            isSunday ? "sunday-header" : ""
          }`}
        >
          <div className="day-number">{day}</div>
          <div className="day-letter">{dayShortName}</div>
        </th>
      );
    }
    return headers;
  };

  const renderDayCells = (row) => {
    const cells = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const viewKey = `day_${day}_view`;
      const morningKey = `day_${day}_morning`;
      const eveningKey = `day_${day}_evening`;
      const callsKey = `day_${day}_calls`;

      const viewValue = row[viewKey] || "View";
      const morningValue = row[morningKey] || "-";
      const eveningValue = row[eveningKey] || "-";
      const callsValue = row[callsKey] ?? "-";
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;

      cells.push(
        <td
          key={`day-${day}-${row.employee_id}`}
          className="clickable-day"
          style={{
            whiteSpace: "pre-line",
            textAlign: "center",
            fontSize: "11px",
          }}
          onClick={() => handleDayClick(row.employee_id, dateStr)}
        >
          {viewValue}
          {"\n"}______
          {"\n"}
          {morningValue}
          {"\n"}______
          {"\n"}
          {eveningValue}
          {"\n"}______
          {"\n"}
          {callsValue}
        </td>
      );
    }
    return cells;
  };

  const handleDayClick = (employeeId, dateStr) => {
    const url = `/pobdatewisedetails?employee_id=${employeeId}&date=${dateStr}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const uniqueEmployees =
    Array.isArray(tableData) && tableData.length > 0
      ? Array.from(new Set(tableData.map((emp) => emp.employee_id))).map(
          (employeeId) =>
            tableData.find((emp) => emp.employee_id === employeeId)
        )
      : [];

  const exportToExcel = () => {
    // For export, we want all data, not just the current page
    const exportAllData = async () => {
      try {
        setIsLoading(true);
        let url = `${BASE_URL}/api/pob-targets/employee-datewise?`;

        if (selectedState) url += `state_id=${selectedState}&`;
        if (selectedDivision && selectedDivision !== "All") {
          const division = divisions.find((d) => d.id == selectedDivision);
          if (division?.division_name)
            url += `division=${encodeURIComponent(division.division_name)}&`;
        }
        if (selectedMonth) url += `month=${selectedMonth}&`;
        if (selectedYear) url += `year=${selectedYear}&`;
        if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;

        // Get all records for export
        url += `skip=0&limit=${totalRecords}`;

        const response = await axios.get(url, {
          headers: getAuthHeaders(),
        });

        const data = response.data.data || response.data;
        const exportData = Array.from(
          new Set(data.map((emp) => emp.employee_id))
        )
          .map((employeeId) =>
            data.find((emp) => emp.employee_id === employeeId)
          )
          .map((row, index) => {
            const rowData = {
              "S.No": index + 1,
              "Emp ID": row.employee_id,
              State: row.state,
              Employee: `${row.employee_name}${
                row.status === "RESIGNED" ? ` (${row.status})` : ""
              }`,
              Designation: row.designation,
              "City/HQ": row.city_hq,
              Division: row.division,
              DOJ: row.final_joining_date
                ? new Date(row.final_joining_date).toLocaleDateString()
                : "-",
              DOR: row.resign_date
                ? new Date(row.resign_date).toLocaleDateString()
                : "-",
              Manager: row.manager || "-",
              Total: row.total || 0,
            };

            for (let day = 1; day <= daysInMonth; day++) {
              rowData[`Day ${day} View`] = row[`day_${day}_view`] || "-";
              rowData[`Day ${day} Morning`] = row[`day_${day}_morning`] || "-";
              rowData[`Day ${day} Evening`] = row[`day_${day}_evening`] || "-";
              rowData[`Day ${day} Calls`] = row[`day_${day}_calls`] || "-";
            }

            return rowData;
          });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "POB Datewise Data");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(dataBlob, `POB_Datewise_${selectedMonth}_${selectedYear}.xlsx`);
      } catch (err) {
        console.error("Failed to export data", err);
        setError("Failed to export data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    exportAllData();
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const tableHtml =
      document.querySelector(".PunchedAttendenceStateWise-table-wrapper")
        ?.innerHTML || "";
    const monthName =
      monthOptions.find((m) => m.value === selectedMonth)?.label || "";

    printWindow.document.write(`
      <html>
        <head>
          <title>POB Datewise Report</title>
          <style>
            @page { size: landscape; margin: 10mm; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .print-header { margin-bottom: 15px; }
            .print-title { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .print-filters { font-size: 14px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: center; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .sunday-header { background-color: #ffe6e6; }
            .day-number { font-weight: bold; }
            .day-letter { font-size: 0.8em; }
            tr { page-break-inside: avoid; }
            .clickable-day { white-space: pre-line; }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="print-title">POB Datewise Report</div>
            <div class="print-filters">
              <div>State: ${
                states.find((s) => s.id == selectedState)?.name || "All"
              }</div>
              <div>Division: ${
                divisions.find((d) => d.id == selectedDivision)
                  ?.division_name || "All"
              }</div>
              <div>Period: ${monthName} ${selectedYear}</div>
              <div>Search: ${searchQuery || "None"}</div>
              <div>Generated on: ${new Date().toLocaleDateString()}</div>
            </div>
          </div>
          ${tableHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="PunchedAttendenceStateWise-main-container">
      {isLoading && <Loader />}

      <div
        className={`main-container ${
          isLoading ? "blur-sm pointer-events-none" : ""
        }`}
      >
        <div className="PunchedAttendenceStateWise-filter-container">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700"> Select </span>
            <span className="text-blue-500">Filter</span>
          </h1>
          <div className="PunchedAttendenceStateWise-filter-row1">
            <label className="PunchedAttendenceStateWise-label">State:</label>
            <select
              className="PunchedAttendenceStateWise-dropdown"
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
              disabled={isLoading}
              required
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>

            <label className="PunchedAttendenceStateWise-label">
              Division:
            </label>
            <select
              className="PunchedAttendenceStateWise-dropdown"
              value={selectedDivision}
              onChange={(e) => handleDivisionChange(e.target.value)}
              disabled={!selectedState || isLoading}
            >
              <option value="All">All</option>
              {divisions.map((division) => (
                <option key={division.id} value={division.id}>
                  {division.name}
                </option>
              ))}
            </select>
          </div>

          <div className="PunchedAttendenceStateWise-filter-row2">
            <label className="PunchedAttendenceStateWise-label">Year:</label>
            <select
              className="PunchedAttendenceStateWise-dropdown"
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value));
                calculateDaysInMonth();
              }}
              disabled={isLoading}
              required
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <label className="PunchedAttendenceStateWise-label">Month:</label>
            <select
              className="PunchedAttendenceStateWise-dropdown"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(parseInt(e.target.value));
                calculateDaysInMonth();
              }}
              disabled={isLoading}
              required
            >
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <button
            className="PunchedAttendenceStateWise-go-button"
            onClick={handleGoClick}
            disabled={isLoading || !selectedState}
          >
            {isLoading ? "Loading..." : "GO"}
          </button>

          {error && (
            <div className="PunchedAttendenceStateWise-error">{error}</div>
          )}
        </div>

        {showContainers && (
          <>
            <div className="PunchedAttendenceStateWise-table-container">
              <div className="PunchedAttendenceStateWise-title3-container">
                <h2 className="PunchedAttendenceStateWise-title3">
                  Punched Attendence
                </h2>
              </div>
              <div className="relative mb-4 flex justify-end">
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex items-center gap-2"
                >
                  {/* Animated Input */}
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search employees..."
                    className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      isSearchExpanded
                        ? "w-64 opacity-100"
                        : "w-0 opacity-0 px-0 py-0"
                    }`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                  />
                  {/* Toggle Button */}
                  <button
                    type="button"
                    onClick={handleExpandSearch}
                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <FaSearch className="w-5 h-5" />
                  </button>
                </form>
              </div>
              <div className="PunchedAttendenceStateWise-table-wrapper">
                <div className="PunchedAttendenceStateWise-table-scroll">
                  <table className="PunchedAttendenceStateWise-table">
                    <thead className="text-white">
                      <tr>
                        <th rowSpan="2">S.No</th>
                        <th rowSpan="2">Emp ID</th>
                        <th rowSpan="2">Employee</th>
                        <th rowSpan="2">State</th>
                        <th rowSpan="2">City/HQ</th>
                        <th rowSpan="2">Designation</th>
                        <th rowSpan="2">Division</th>
                        <th rowSpan="2">DOJ</th>
                        <th rowSpan="2">DOR</th>
                        <th rowSpan="2">Manager</th>
                        <th rowSpan="2"></th>
                        {renderDayHeaders()}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(uniqueEmployees).length > 0 ? (
                        Array.from(uniqueEmployees).map((row, index) => (
                          <tr key={index}>
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td>{row.employee_id}</td>
                            <td>
                              {row.employee_name}
                              {row.status === "RESIGNED" && ` (${row.status})`}
                            </td>
                            <td>{row.state}</td>
                            <td>{row.city_hq}</td>
                            <td>{row.designation}</td>
                            <td>{row.division}</td>
                            <td>
                              {row.final_joining_date
                                ? new Date(
                                    row.final_joining_date
                                  ).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "-"}
                            </td>
                            <td>
                              {row.resign_date
                                ? new Date(row.resign_date).toLocaleDateString(
                                    "en-GB",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    }
                                  )
                                : "-"}
                            </td>
                            <td>{row.manager || "-"}</td>
                            <td>
                              <strong>
                                GPS
                                <br />
                                ______
                                <br />
                                Morning
                                <br />
                                ______
                                <br />
                                Evening
                                <br />
                                ______
                                <br />
                                Calls
                              </strong>
                            </td>
                            {renderDayCells(row)}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={11 + daysInMonth}
                            className="PunchedAttendenceStateWise-no-results"
                          >
                            No data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                <div className="flex items-center mb-2 sm:mb-0">
                  <label
                    htmlFor="itemsPerPage"
                    className="mr-2 font-medium text-sm text-gray-700"
                  >
                    View
                  </label>
                  <select
                    id="itemsPerPage"
                    className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="30">30</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 font-medium">
                    {(currentPage - 1) * itemsPerPage + 1}-
                    {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                    {totalRecords}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage * itemsPerPage >= totalRecords}
                    className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            <div className="PunchedAttendenceStateWise-export-buttons">
              <button
                className="PunchedAttendenceStateWise-print-button"
                onClick={handlePrint}
              >
                <FaPrint className="button-icon" />
                Print
              </button>
              <button
                className="PunchedAttendenceStateWise-export-button"
                onClick={exportToExcel}
              >
                <FaFileExcel className="button-icon" />
                Export to Excel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PunchedAttendenceStateWise;
